import React, { useState } from 'react';
import { Star } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from './ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReviewDialog = ({ open, onOpenChange }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setRating(0);
    setHover(0);
    setName('');
    setRole('');
    setComment('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { name, role, rating, comment });
      toast.success('Thank you for your review!', {
        description: 'Your review has been submitted and will appear once approved.',
        duration: 6000,
      });
      reset();
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let description = 'Failed to submit your review. Please try again.';
      if (typeof detail === 'string') description = detail;
      else if (Array.isArray(detail)) description = detail.map((d) => (d?.msg || 'Invalid input').replace(/^Value error, /, '')).join(', ');
      toast.error('Something went wrong', { description, duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a1a] border-white/10 text-white sm:max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">Leave a Review</DialogTitle>
          <DialogDescription className="text-white/60">
            Share your experience. Your review will appear on the site once approved.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          <div>
            <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Your Rating *</label>
            <div className="flex gap-2" data-testid="review-rating">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`review-star-${s}`}
                  onClick={() => setRating(s)}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(0)}
                  className="transition-transform duration-200 hover:scale-110"
                >
                  <Star className={`h-8 w-8 ${(hover || rating) >= s ? 'fill-[#B8956A] text-[#B8956A]' : 'text-white/30'}`} />
                </button>
              ))}
            </div>
          </div>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            maxLength={100}
            data-testid="review-name-input"
            placeholder="Your Name *"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] h-12"
          />
          <Input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            maxLength={100}
            data-testid="review-role-input"
            placeholder="Event type / location (optional)"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] h-12"
          />
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            minLength={10}
            maxLength={600}
            rows={4}
            data-testid="review-comment-input"
            placeholder="Tell us about your experience *"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A]"
          />
          <Button
            type="submit"
            disabled={submitting}
            data-testid="review-submit-btn"
            className="w-full bg-[#B8956A] hover:bg-[#9a7a52] text-white uppercase tracking-wider py-6 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
